const PRESENCE_STATES = new Set(['connected', 'disconnected', 'unknown']);

const PRESENCE_FIELDS = [
  'status',
  'dbStatus',
  'presenceState',
  'state',
  'statusSource',
  'version',
  'detectedAt',
  'lastPacketAt',
  'lastConnectedAt',
  'lastDisconnectedAt',
  'instanceId',
  'lastHeartbeatAt',
  'tcpConnected',
  'heartbeatFresh',
  'computedOnline',
  'computedStatus',
  'onlineReason',
  'lastHeartbeatAgeSeconds',
  'onlineWindowMs',
  'connectionDisplayStatus',
  'connectionDisplaySource',
  'stagingRuntimeIsolated'
];

const statePriority = {
  connected: 1,
  unknown: 2,
  disconnected: 3
};

const normalizePresenceState = (value) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return PRESENCE_STATES.has(normalized) ? normalized : null;
};

const stateFromStatus = (value) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'online' || normalized === 'connected') return 'connected';
  if (normalized === 'offline' || normalized === 'disconnected') return 'disconnected';
  if (normalized === 'unknown') return 'unknown';
  return null;
};

const resolvePresenceState = (unit) => normalizePresenceState(unit?.presenceState)
  || normalizePresenceState(unit?.state)
  || stateFromStatus(unit?.computedStatus)
  || (unit?.computedOnline === true ? 'connected' : null)
  || stateFromStatus(unit?.status)
  || 'unknown';

const toSafeVersion = (value) => {
  const version = Number(value);
  return Number.isSafeInteger(version) && version >= 0 ? version : null;
};

const toTimestamp = (value) => {
  const timestamp = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
};

const comparePresence = (left, right) => {
  const leftVersion = toSafeVersion(left?.version);
  const rightVersion = toSafeVersion(right?.version);

  if (leftVersion !== null && rightVersion !== null && leftVersion !== rightVersion) {
    return leftVersion > rightVersion ? 1 : -1;
  }
  if (leftVersion !== null && rightVersion === null) return 1;
  if (leftVersion === null && rightVersion !== null) return -1;

  const leftDetectedAt = toTimestamp(left?.detectedAt);
  const rightDetectedAt = toTimestamp(right?.detectedAt);
  if (leftDetectedAt !== null && rightDetectedAt !== null && leftDetectedAt !== rightDetectedAt) {
    return leftDetectedAt > rightDetectedAt ? 1 : -1;
  }
  if (leftDetectedAt !== null && rightDetectedAt === null) return 1;
  if (leftDetectedAt === null && rightDetectedAt !== null) return -1;

  const stateDifference = statePriority[resolvePresenceState(left)] - statePriority[resolvePresenceState(right)];
  if (stateDifference !== 0) return stateDifference > 0 ? 1 : -1;
  return 0;
};

const applyPresenceWinner = (target, winner) => {
  PRESENCE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(winner, field)) {
      target[field] = winner[field];
    } else {
      delete target[field];
    }
  });
  return target;
};

export const normalizePresenceUnit = (unit = {}) => {
  const presenceState = resolvePresenceState(unit);
  return {
    ...unit,
    presenceState,
    state: presenceState,
    computedOnline: presenceState === 'connected',
    computedStatus: presenceState === 'connected'
      ? 'Online'
      : presenceState === 'disconnected' ? 'Offline' : 'Unknown'
  };
};

export const mergeUnitPresence = (current = {}, incoming = {}) => {
  const currentPresence = normalizePresenceUnit(current);
  const incomingPresence = normalizePresenceUnit(incoming);
  const comparison = comparePresence(incomingPresence, currentPresence);
  const winner = comparison >= 0 ? incomingPresence : currentPresence;
  return normalizePresenceUnit(applyPresenceWinner({ ...current, ...incoming }, winner));
};

export const mergeDevicePresenceEvent = (currentUnits = [], message = {}) => {
  const deviceId = message.deviceId || message.data?.deviceId;
  if (!deviceId) return currentUnits;

  const index = currentUnits.findIndex((unit) => unit.deviceId === deviceId);
  if (index === -1) {
    return [normalizePresenceUnit({
      id: message.data?.id ?? message.id ?? `realtime-${deviceId}`,
      deviceId,
      deviceName: message.data?.deviceName || deviceId,
      ...message.data
    }), ...currentUnits];
  }

  const incoming = normalizePresenceUnit({ deviceId, ...message.data });

  return currentUnits.map((unit, unitIndex) => (
    unitIndex === index ? mergeUnitPresence(unit, incoming) : unit
  ));
};

export const mergePolledUnits = (currentUnits = [], polledUnits = []) => {
  const currentByDeviceId = new Map(
    currentUnits.filter((unit) => unit?.deviceId).map((unit) => [unit.deviceId, unit])
  );
  return polledUnits.map((unit) => {
    const incoming = normalizePresenceUnit(unit);
    const current = currentByDeviceId.get(incoming.deviceId);
    return current ? mergeUnitPresence(current, incoming) : incoming;
  });
};

export const getUnitConnectionStatus = (unit) => {
  if (unit?.connectionDisplayStatus) return unit.connectionDisplayStatus;
  const state = resolvePresenceState(unit);
  if (state === 'connected') return 'Online';
  if (state === 'disconnected') return 'Offline';
  return 'Unknown';
};

export const getPresenceLastDataAt = (unit) => unit?.lastPacketAt || unit?.lastHeartbeatAt || null;

export { comparePresence };
